import { useId, useState, type ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Input, type InputProps } from "@/shared/components/ui/Input";
import { Label } from "@/shared/components/ui/Label";

export interface PasswordFormFieldProps extends Omit<InputProps, "type"> {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
}

export function PasswordFormField({
  label,
  hint,
  error,
  id,
  className,
  ...inputProps
}: PasswordFormFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={inputId} className="mx-4">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={inputId}
          type={visible ? "text" : "password"}
          aria-invalid={error ? true : undefined}
          {...inputProps}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2",
            "inline-flex items-center justify-center rounded-lg p-1.5",
            "text-text-muted hover:text-text",
            "focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          )}
        >
          <Icon aria-hidden className="h-4 w-4" />
        </button>
      </div>
      {error ? (
        <p className="mx-4 type-body-sm text-danger-strong">{error}</p>
      ) : hint ? (
        <p className="mx-4 type-body-sm text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
